import React, { useState, useEffect } from 'react';
import { 
  Calendar, TrendingUp, AlertCircle, CheckCircle, 
  ChevronRight, Package, Sun, Cloud, Snowflake, Flower,
  ShoppingCart, DollarSign, BarChart3, Target
} from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';

interface WeekData {
  week: number;
  startDate: string;
  endDate: string;
  season: string;
  event: string;
  lastYearSales: number;
  forecastSales: number;
  suggestedOrder: number;
  currentStock: number;
  recommendedActions: string[];
}

const MDDialogFlow: React.FC = () => {
  const [currentWeek, setCurrentWeek] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [dialogStep, setDialogStep] = useState(0);
  const [analysisData, setAnalysisData] = useState<WeekData | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // 季節アイコンの取得
  const getSeasonIcon = (season: string) => {
    switch(season) {
      case '春': return <Flower size={16} style={{ color: '#FF69B4' }} />;
      case '夏': return <Sun size={16} style={{ color: '#FFA500' }} />;
      case '秋': return <Cloud size={16} style={{ color: '#8B4513' }} />;
      case '冬': return <Snowflake size={16} style={{ color: '#4169E1' }} />;
      default: return null;
    }
  };

  // 今週のデータを生成（モック）
  const generateWeekData = (week: number): WeekData => {
    const baseDate = new Date(2025, 0, 1); // 2025年1月1日から開始
    const weekStart = new Date(baseDate.getTime() + (week - 1) * 7 * 24 * 60 * 60 * 1000);
    const weekEnd = new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000);
    
    // 季節判定
    const month = weekStart.getMonth() + 1;
    let season = '冬';
    let event = '';
    
    if (month >= 3 && month <= 5) {
      season = '春';
      if (week === 10) event = 'ホワイトデー';
      if (week === 12) event = '新生活';
      if (week === 18) event = 'ゴールデンウィーク';
    } else if (month >= 6 && month <= 8) {
      season = '夏';
      if (week === 24) event = '梅雨';
      if (week === 28) event = '夏休み開始';
      if (week === 32) event = 'お盆';
    } else if (month >= 9 && month <= 11) {
      season = '秋';
      if (week === 38) event = '秋分の日';
      if (week === 42) event = 'ハロウィン';
      if (week === 46) event = '七五三';
    } else {
      season = '冬';
      if (week === 1) event = '正月';
      if (week === 6) event = 'バレンタイン';
      if (week === 51) event = 'クリスマス';
      if (week === 52) event = '年末';
    }
    
    // 売上予測（季節・イベントによる変動）
    const baseSales = 5000000;
    let multiplier = 1.0;
    if (event) multiplier *= 1.5;
    if (season === '夏' || season === '冬') multiplier *= 1.2;
    
    const lastYearSales = baseSales * multiplier * (0.9 + Math.random() * 0.2);
    const forecastSales = lastYearSales * (1.05 + Math.random() * 0.1);
    
    return {
      week,
      startDate: `${weekStart.getMonth() + 1}/${weekStart.getDate()}`,
      endDate: `${weekEnd.getMonth() + 1}/${weekEnd.getDate()}`,
      season,
      event,
      lastYearSales,
      forecastSales,
      suggestedOrder: Math.floor(forecastSales / 5000) * 1.1,
      currentStock: Math.floor(Math.random() * 1000) + 500,
      recommendedActions: [
        event ? `${event}商戦に向けた特設コーナー設置` : '',
        season === '夏' ? '冷蔵・冷凍商品の在庫増強' : '',
        season === '冬' ? '温かい飲料・食品の品揃え強化' : '',
        '売れ筋商品のフェース拡大',
        '季節商品の先行発注'
      ].filter(Boolean)
    };
  };

  // 分析開始
  const startAnalysis = () => {
    setIsAnalyzing(true);
    setDialogStep(1);
    
    // 分析シミュレーション
    setTimeout(() => {
      const data = generateWeekData(currentWeek);
      setAnalysisData(data);
      setIsAnalyzing(false);
      setDialogStep(2);
      
      // 計画生成アクション
      console.log('📅 52週MD計画を生成しました！', data);
      alert(`✅ 52週MD計画を生成しました！\n\n計画内容:\n- 期間: ${data.startDate} 〜 ${data.endDate}\n- 予測売上: ¥${data.forecastSales.toLocaleString()}\n- 推奨発注量: ${data.suggestedOrder.toLocaleString()}点\n- 季節: ${data.season}\n- イベント: ${data.event}`);
    }, 2000);
  };

  // 提案を承認
  const approveRecommendation = () => {
    setDialogStep(3);
    // 実際の発注処理をシミュレート
    setTimeout(() => {
      setDialogStep(4);
    }, 1500);
  };

  return (
    <div className="p-6 space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar size={24} style={{ color: colors.accent.blue }} />
          <h2 className="text-xl font-semibold" style={{ color: colors.text.primary }}>
            52週MD計画 - 週次仕入れ最適化
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm" style={{ color: colors.text.secondary }}>現在:</span>
          <Badge variant="primary">第{currentWeek}週</Badge>
        </div>
      </div>

      {/* カテゴリー選択 */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium" style={{ color: colors.text.primary }}>
          対象カテゴリー:
        </label>
        <select 
          className="px-3 py-2 text-sm border rounded-lg"
          style={{ borderColor: colors.ui.border }}
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="all">全カテゴリー</option>
          <option value="beverage">飲料</option>
          <option value="snacks">菓子・スナック</option>
          <option value="fresh">生鮮食品</option>
          <option value="frozen">冷凍食品</option>
          <option value="daily">日配品</option>
        </select>
      </div>

      {/* 対話フロー */}
      <div className="space-y-4">
        {/* Step 0: 初期状態 */}
        {dialogStep === 0 && (
          <Card className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: colors.accent.blue + '20' }}>
                <BarChart3 size={20} style={{ color: colors.accent.blue }} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-2" style={{ color: colors.text.primary }}>
                  第{currentWeek}週の仕入れ計画を最適化しましょう
                </h3>
                <p className="text-sm mb-4" style={{ color: colors.text.secondary }}>
                  過去の販売データと季節性、イベントを考慮して、最適な仕入れ量を提案します。
                  在庫の過不足を防ぎ、売上機会の最大化を支援します。
                </p>
                <Button onClick={startAnalysis} variant="primary">
                  <TrendingUp size={16} />
                  計画を生成
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Step 1: 分析中 */}
        {dialogStep === 1 && (
          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center animate-pulse"
                style={{ backgroundColor: colors.accent.blue + '20' }}>
                <BarChart3 size={20} style={{ color: colors.accent.blue }} />
              </div>
              <div>
                <h3 className="font-semibold mb-1" style={{ color: colors.text.primary }}>
                  分析中...
                </h3>
                <p className="text-sm" style={{ color: colors.text.secondary }}>
                  過去52週のデータと季節性を分析しています
                </p>
              </div>
            </div>
            <div className="mt-4 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 animate-pulse" style={{ width: '60%' }} />
            </div>
          </Card>
        )}

        {/* Step 2: 分析結果と提案 */}
        {dialogStep === 2 && analysisData && (
          <>
            <Card className="p-6 border-l-4" style={{ borderLeftColor: colors.accent.blue }}>
              <div className="flex items-start gap-4">
                <CheckCircle size={24} style={{ color: colors.support.success }} />
                <div className="flex-1">
                  <h3 className="font-semibold mb-3" style={{ color: colors.text.primary }}>
                    分析が完了しました
                  </h3>
                  
                  {/* 週の概要 */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">第{analysisData.week}週</span>
                        <span className="text-sm text-gray-600">
                          {analysisData.startDate} - {analysisData.endDate}
                        </span>
                        {getSeasonIcon(analysisData.season)}
                        <span className="text-sm">{analysisData.season}</span>
                      </div>
                      {analysisData.event && (
                        <Badge variant="warning">{analysisData.event}</Badge>
                      )}
                    </div>
                  </div>

                  {/* 売上予測 */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <DollarSign size={16} style={{ color: colors.accent.blue }} />
                        <span className="text-sm font-medium">売上予測</span>
                      </div>
                      <p className="text-lg font-bold" style={{ color: colors.accent.blue }}>
                        ¥{(analysisData.forecastSales / 1000000).toFixed(1)}M
                      </p>
                      <p className="text-xs text-gray-600">
                        前年比 +{((analysisData.forecastSales / analysisData.lastYearSales - 1) * 100).toFixed(1)}%
                      </p>
                    </div>
                    
                    <div className="p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Package size={16} style={{ color: colors.support.success }} />
                        <span className="text-sm font-medium">推奨発注数</span>
                      </div>
                      <p className="text-lg font-bold" style={{ color: colors.support.success }}>
                        {analysisData.suggestedOrder.toLocaleString()} 個
                      </p>
                      <p className="text-xs text-gray-600">
                        現在庫: {analysisData.currentStock.toLocaleString()} 個
                      </p>
                    </div>
                  </div>

                  {/* 推奨アクション */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium mb-2" style={{ color: colors.text.primary }}>
                      推奨アクション:
                    </h4>
                    <ul className="space-y-1">
                      {analysisData.recommendedActions.map((action, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <ChevronRight size={14} style={{ color: colors.accent.blue }} />
                          <span style={{ color: colors.text.secondary }}>{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* 季節性アドバイス */}
                  {analysisData.event && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                      <div className="flex items-start gap-2">
                        <AlertCircle size={16} className="text-yellow-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-yellow-800">
                            {analysisData.event}期間の注意点
                          </p>
                          <p className="text-xs text-yellow-700 mt-1">
                            通常より150%の需要が見込まれます。早期の発注と売場拡大を推奨します。
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button onClick={approveRecommendation} variant="primary">
                      <CheckCircle size={16} />
                      提案を承認
                    </Button>
                    <Button variant="secondary" onClick={() => setDialogStep(0)}>
                      再分析
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </>
        )}

        {/* Step 3: 処理中 */}
        {dialogStep === 3 && (
          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="animate-spin">
                <Package size={24} style={{ color: colors.accent.blue }} />
              </div>
              <div>
                <h3 className="font-semibold mb-1" style={{ color: colors.text.primary }}>
                  発注処理中...
                </h3>
                <p className="text-sm" style={{ color: colors.text.secondary }}>
                  推奨内容に基づいて発注データを生成しています
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Step 4: 完了 */}
        {dialogStep === 4 && analysisData && (
          <Card className="p-6 border-l-4" style={{ borderLeftColor: colors.support.success }}>
            <div className="flex items-start gap-4">
              <CheckCircle size={24} style={{ color: colors.support.success }} />
              <div className="flex-1">
                <h3 className="font-semibold mb-2" style={{ color: colors.text.primary }}>
                  発注計画が確定しました
                </h3>
                <p className="text-sm mb-4" style={{ color: colors.text.secondary }}>
                  第{analysisData.week}週（{analysisData.startDate} - {analysisData.endDate}）の発注計画を確定しました。
                </p>
                <div className="bg-green-50 p-4 rounded-lg mb-4">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">発注数量</p>
                      <p className="font-bold">{analysisData.suggestedOrder.toLocaleString()} 個</p>
                    </div>
                    <div>
                      <p className="text-gray-600">予想売上</p>
                      <p className="font-bold">¥{(analysisData.forecastSales / 1000000).toFixed(1)}M</p>
                    </div>
                    <div>
                      <p className="text-gray-600">納品予定日</p>
                      <p className="font-bold">{analysisData.startDate}</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button 
                    onClick={() => {
                      setCurrentWeek(currentWeek + 1);
                      setDialogStep(0);
                      setAnalysisData(null);
                    }}
                    variant="primary"
                  >
                    <ChevronRight size={16} />
                    次週の分析へ
                  </Button>
                  <Button variant="secondary">
                    <Target size={16} />
                    棚割り最適化へ
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* 週次ナビゲーション */}
      <div className="flex items-center justify-center gap-2">
        <Button
          variant="secondary"
          size="small"
          onClick={() => setCurrentWeek(Math.max(1, currentWeek - 1))}
          disabled={currentWeek === 1}
        >
          前週
        </Button>
        <div className="flex gap-1">
          {[...Array(5)].map((_, i) => {
            const week = currentWeek - 2 + i;
            if (week < 1 || week > 52) return null;
            return (
              <button
                key={week}
                onClick={() => {
                  setCurrentWeek(week);
                  setDialogStep(0);
                }}
                className={`w-8 h-8 rounded text-xs font-medium ${
                  week === currentWeek ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {week}
              </button>
            );
          })}
        </div>
        <Button
          variant="secondary"
          size="small"
          onClick={() => setCurrentWeek(Math.min(52, currentWeek + 1))}
          disabled={currentWeek === 52}
        >
          次週
        </Button>
      </div>
    </div>
  );
};

export default MDDialogFlow;
